import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Camera,
  ChevronRight,
  Layers,
  Loader2,
  Package,
  PackageOpen,
  ScanSearch,
  X,
} from "lucide-react";
import React, { Suspense, useRef, useState } from "react";

import {
  getLabelInfoConfig,
  scanLabel,
  type LabelPacking,
  type LabelScan,
  type LabelScanItem,
} from "@/api/label_info";
import type { ResolveRequest } from "@/api/order_check";
import { fmtDateTime } from "@/lib/format";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { parseScan } from "@/lib/order-scan";
import { GOODS_AND_QR_FORMATS } from "@/lib/scan-formats";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { apiErrorText, useGoBack } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { NomenclatureCard } from "@/pages/label-info/NomenclatureCard";

/**
 * «Что это?» — справка по скану любой этикетки (EAN13, GS1, ЧЗ, SSCC коробки).
 *
 * Экран ждёт скан (ТСД или камера). Товар — сразу карточка номенклатуры:
 * остаток по юрлицам/складам/партиям и расход за 90 дней. Коробка портала —
 * её состав, тап по строке открывает карточку (позиция одна — карточка сразу).
 * Короб/паллета с упаковки — то же плюс блок «где, кто, когда собрал».
 * Не опознано — жёлтая плашка с текстом резолвера. Новый скан заменяет всё.
 * Раздел только читает: ни в БД портала, ни в 1С ничего не пишет.
 */

type Banner = { tone: "warn" | "error"; title: string; detail: string; raw?: string };

interface Picked {
  item: LabelScanItem;
  /** Номер скана: в ключе запроса карточки — повторный скан перечитывает остаток. */
  scanId: number;
}

export function LabelInfoPage(): React.ReactElement {
  usePreloadChunk(CameraScanner);
  const goBack = useGoBack("/");
  const configQ = useQuery({
    queryKey: ["label-info-config"],
    queryFn: getLabelInfoConfig,
    staleTime: 5 * 60_000,
  });
  const cameraAllowed =
    configQ.data?.scan_method === "camera" || configQ.data?.scan_method === "both";

  const [banner, setBanner] = useState<Banner | null>(null);
  const [result, setResult] = useState<LabelScan | null>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const scanSeq = useRef(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  useBackGuard(cameraOpen, () => setCameraOpen(false));
  // Аппаратная «назад» из карточки коробочной позиции — к составу коробки.
  const boxItemOpen = !!picked && !!result && result.items.length > 1;
  useBackGuard(boxItemOpen, () => setPicked(null));

  const scanM = useMutation({
    mutationFn: (body: ResolveRequest) => scanLabel(body),
    onSuccess: (out) => {
      scanSeq.current += 1;
      const id = scanSeq.current;
      setResult(out);
      if (!out.matched) {
        setPicked(null);
        setBanner({
          tone: "warn",
          // Короб/паллета с упаковки ОПОЗНАНЫ (аннулированы, чужая база…) —
          // «не опознана» соврало бы; причина — в тексте бэка.
          title: out.packing
            ? out.packing.kind === "pallet"
              ? "Паллета с упаковки"
              : "Короб с упаковки"
            : out.sscc
              ? "Коробка не опознана"
              : "Код не опознан",
          detail: out.message,
          raw: out.raw,
        });
        navigator.vibrate?.([60, 60, 60]);
        return;
      }
      // Вскрытый короб упаковки опознан, но засчитывать его — остатком:
      // предупреждение видно сразу, над карточкой.
      setBanner(
        out.notice ? { tone: "warn", title: "Грузоместо неполное", detail: out.notice, raw: out.raw } : null,
      );
      navigator.vibrate?.(40);
      setPicked(out.items.length === 1 ? { item: out.items[0]!, scanId: id } : null);
    },
    onError: (e, body) => {
      // Старая карточка под красной плашкой читалась бы как ответ на этот скан.
      setResult(null);
      setPicked(null);
      setBanner({ tone: "error", title: "Скан не обработан", detail: apiErrorText(e), raw: body.raw });
      navigator.vibrate?.([60, 60, 60]);
    },
  });

  function handleScan(raw: string) {
    if (scanM.isPending) return;
    const req = toResolveRequest(parseScan(raw));
    if (!req) {
      setBanner({
        tone: "warn",
        title: "Код не опознан",
        detail: "Это не ШК/GTIN/GS1/DataMatrix товара и не номер коробки",
        raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    scanM.mutate(req);
  }

  const capture = useScanCapture({
    enabled: !cameraOpen && !scanM.isPending,
    onScan: handleScan,
    onBlocked: (raw) => {
      if (cameraOpen) return;
      setBanner({
        tone: "error",
        title: "Скан не принят",
        detail: "Предыдущий скан ещё обрабатывается — отсканируйте ещё раз.",
        raw,
      });
    },
  });

  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      <div className="flex h-[46px] items-center gap-1 bg-slate-900 px-1 text-white">
        <button
          type="button"
          onClick={goBack}
          className="flex size-9 items-center justify-center"
          aria-label="Назад"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="flex-1 text-[14px] font-bold">Что это?</div>
      </div>

      <input ref={capture.scanInputRef} {...capture.scanInputProps} className="sr-only" />

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 pb-24">
        {configQ.isError && (
          <p className="rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
            Способ сканирования не загрузился: {apiErrorText(configQ.error)}. Камера недоступна,
            сканер ТСД работает.
          </p>
        )}

        {banner && <ScanAlert banner={banner} onClose={() => setBanner(null)} />}

        {scanM.isPending ? (
          <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-[13px]">
            <Loader2 className="size-4 animate-spin text-sky-600" />
            <span className="text-sky-700">Ищем…</span>
          </div>
        ) : (
          !result?.matched && !banner && <Waiting />
        )}

        {result?.matched && result.box && <BoxHeader scan={result} />}
        {/* Шапка упаковки — и при отказе (аннулирован, чужая база): кто, где и
            когда собрал, видно и у снятой этикетки. */}
        {result?.packing && (
          <PackingHeader packing={result.packing} positions={result.items.length} />
        )}

        {result?.matched && picked && (
          <>
            {result.items.length > 1 && (
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="flex items-center gap-1 text-[13px] font-semibold text-sky-700"
              >
                <ArrowLeft className="size-4" />{" "}
                {result.packing?.kind === "pallet" ? "К составу паллеты" : "К составу коробки"}
              </button>
            )}
            <NomenclatureCard
              nomenclatureRef={picked.item.nomenclature_ref}
              characteristicRef={picked.item.characteristic?.ref ?? ""}
              partia={picked.item.partia}
              scanId={picked.scanId}
            />
          </>
        )}

        {result?.matched && !picked && (
          <BoxItems
            isBox={!!result.box || !!result.packing}
            items={result.items}
            onPick={(item) => setPicked({ item, scanId: scanSeq.current })}
          />
        )}
      </div>

      {cameraAllowed && !cameraOpen && (
        <button
          type="button"
          onClick={() => setCameraOpen(true)}
          className="fixed bottom-6 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Камера
        </button>
      )}
      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              // Рядом и заводские EAN13/DataMatrix, и наклейки портала
              // (DataMatrix коробки, QR приёмки).
              formats={GOODS_AND_QR_FORMATS}
              onDetect={(raw) => {
                if (scanM.isPending) return;
                setCameraOpen(false);
                handleScan(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

function Waiting(): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-500">
      <ScanSearch className="size-12 text-slate-400" />
      <div className="text-[15px] font-semibold">Отсканируйте этикетку</div>
      <div className="text-[12px]">Товар (ШК, GS1, Честный знак) или коробку</div>
    </div>
  );
}

function ScanAlert({ banner, onClose }: { banner: Banner; onClose: () => void }): React.ReactElement {
  const warn = banner.tone === "warn";
  return (
    <div
      role="alert"
      className={cn(
        "rounded-xl border-2 p-3",
        warn ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className={cn("text-[17px] font-bold", warn ? "text-amber-800" : "text-red-700")}>
            {banner.title}
          </div>
          <div className={cn("mt-1 text-[13px]", warn ? "text-amber-900" : "text-red-900")}>
            {banner.detail}
          </div>
          {banner.raw && (
            <div className="mt-1 break-all font-mono text-[11px] text-slate-500">{banner.raw}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex size-8 items-center justify-center"
          aria-label="Скрыть"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

function BoxHeader({ scan }: { scan: LabelScan }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3">
      <PackageOpen className="size-6 shrink-0 text-sky-700" />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-bold">Коробка</div>
        <div className="break-all font-mono text-[12px] text-slate-600">{scan.box?.sscc}</div>
      </div>
      <div className="text-right text-[12px] text-slate-600">позиций: {scan.items.length}</div>
    </div>
  );
}

/** Короб/паллета с упаковки: где, кто и когда собрал (снимок chz-station). */
function PackingHeader({
  packing,
  positions,
}: {
  packing: LabelPacking;
  positions: number;
}): React.ReactElement {
  const pallet = packing.kind === "pallet";
  const rows: [string, string][] = pallet
    ? [
        ["Коробов", String(packing.boxes_count)],
        ["Место", packing.station || "—"],
        ["Собрал", packing.packed_by_name || "—"],
        ["Когда", fmtDateTime(packing.packed_at) || "—"],
      ]
    : [
        ["Место", packing.station || "—"],
        ["Собрал", packing.packed_by_name || "—"],
        ["Когда", fmtDateTime(packing.packed_at) || "—"],
        ["Паллета", packing.pallet_code || "не на паллете"],
        ["Кодов штук", String(packing.codes_count)],
      ];
  if (packing.taken_count > 0) {
    rows.push(["Вынуто штук", String(packing.taken_count)]);
  }
  if (packing.opened_at) {
    rows.push(["Вскрыт", fmtDateTime(packing.opened_at) || "—"]);
  }
  const Icon = pallet ? Layers : Package;
  return (
    <div data-testid="packing-header" className="rounded-xl border border-sky-200 bg-sky-50 p-3">
      <div className="flex items-center gap-2">
        <Icon className="size-6 shrink-0 text-sky-700" />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold">
            {pallet ? "Паллета с упаковки" : "Короб с упаковки"}
          </div>
          <div className="break-all font-mono text-[12px] text-slate-600">{packing.code}</div>
        </div>
        {positions > 1 && (
          <div className="text-right text-[12px] text-slate-600">позиций: {positions}</div>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-slate-500">{label}</dt>
            <dd className="break-all font-medium text-slate-800">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function BoxItems({
  isBox,
  items,
  onPick,
}: {
  isBox: boolean;
  items: LabelScanItem[];
  onPick: (item: LabelScanItem) => void;
}): React.ReactElement {
  if (items.length === 0) {
    return (
      <p className="rounded-xl border bg-white p-4 text-center text-[14px] text-slate-500">
        {isBox ? "Коробка пустая" : "Код опознан, но позиций в ответе нет"}
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <button
          key={`${it.nomenclature_ref}:${it.characteristic?.ref ?? ""}:${it.partia?.ref ?? ""}:${i}`}
          type="button"
          data-testid="box-item"
          onClick={() => onPick(it)}
          className="flex w-full items-center gap-2 rounded-lg border bg-white p-2 text-left"
        >
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[15px] font-bold">{it.article || "—"}</div>
            <div className="line-clamp-2 text-[12px] text-slate-600">{it.name}</div>
            {(it.characteristic || it.partia) && (
              <div className="text-[11px] text-slate-500">
                {[it.characteristic?.name, it.partia && `партия ${it.partia.name}`]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
          <span className="shrink-0 font-mono text-[18px] font-bold">{fmtQty(it.qty)}</span>
          <ChevronRight className="size-4 shrink-0 text-slate-400" />
        </button>
      ))}
    </div>
  );
}
